import { DotCMSFields } from '@/types/OasAppContextTypes';

export type DshsItem = {
  hostName: string;
  modDate: string;
  accessPointId: string;
  title: string;
  baseType: string;
  inode: string;
  archived: boolean;
  host: string;
  working: boolean;
  locked: boolean;
  stInode: string;
  contentType: string;
  live: boolean;
  dshsEnglish: string;
  owner: string;
  identifier: string;
  languageId: number;
  dshsSpanish: string;
  accessPoint: string;
  url: string;
  titleImage: string;
  modUserName: string;
  folder: string;
  hasTitleImage: boolean;
  sortOrder: number;
  modUser: string;
};

export type DshsResponse = {
  contentlets: DshsItem[];
};

const getDshsQuery = ({
  dotCmsHostId,
  dotCmsHostName,
  accessPoint,
}: {
  dotCmsHostId: string;
  dotCmsHostName: string;
  accessPoint?: string;
}): string => {
  const hostName = dotCmsHostName;
  const baseQuery = `https://${hostName}/api/content/render/false/type/json/query/+contentType:Dshs +(conhost:${dotCmsHostId} conhost:SYSTEM_HOST) `;
  const accessPointQuery = accessPoint ? `+Dshs.accessPoint:*${accessPoint}* ` : '';
  const querySuffix = `+languageId:1 +deleted:false +working:true/orderby/modDate desc`;
  return `${baseQuery}${accessPointQuery}${querySuffix}`;
};

const fetchDshs = async (query: string): Promise<{ data: DshsItem[]; hasResults: boolean }> => {
  try {
    const dshsResponse: DshsResponse = (await fetch(query).then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to fetch DSHS data: ${response.statusText}`);
      }
      return response.json();
    })) as DshsResponse;

    return {
      data: dshsResponse?.contentlets,
      hasResults: dshsResponse?.contentlets.length > 0,
    };
  } catch (error) {
    console.error('Error fetching DSHS data:', error);
    return { data: [], hasResults: false };
  }
};

export const getDshs = async ({
  dotCmsHostId,
  dotCmsHostName,
  mbcDefaultAccesspoint,
  mbcLocationAccesspoint,
  coreHostId,
}: DotCMSFields): Promise<{
  dshsEnglish?: string;
  dshsSpanish?: string;
}> => {
  if (dotCmsHostName) {
    const locationAccesspoint = Array.isArray(mbcLocationAccesspoint)
      ? mbcLocationAccesspoint[0]
      : mbcLocationAccesspoint;
    const defaultAccesspoint = Array.isArray(mbcDefaultAccesspoint)
      ? mbcDefaultAccesspoint[0]
      : mbcDefaultAccesspoint;

    let dshsResult: { data: DshsItem[]; hasResults: boolean } = { data: [], hasResults: false };

    if (locationAccesspoint) {
      dshsResult = await fetchDshs(
        getDshsQuery({ dotCmsHostId, dotCmsHostName, accessPoint: locationAccesspoint })
      );
    }

    if (defaultAccesspoint && !dshsResult.hasResults) {
      dshsResult = await fetchDshs(
        getDshsQuery({ dotCmsHostId, dotCmsHostName, accessPoint: defaultAccesspoint })
      );
    }

    if (!dshsResult.hasResults) {
      dshsResult = await fetchDshs(getDshsQuery({ dotCmsHostId, dotCmsHostName }));
    }

    if (!dshsResult.hasResults && coreHostId) {
      dshsResult = await fetchDshs(getDshsQuery({ dotCmsHostId: coreHostId, dotCmsHostName }));
    }

    const results: DshsItem[] = dshsResult.hasResults ? dshsResult.data : [];
    const firstItem = results?.[0] || {};

    return {
      dshsEnglish: firstItem.dshsEnglish,
      dshsSpanish: firstItem.dshsSpanish,
    };
  } else {
    console.error('DotCMS host name is not defined');
    return { dshsEnglish: undefined, dshsSpanish: undefined };
  }
};
